import type { EntityType } from '@tunasong/schemas'
import { entityTypeNames } from '@tunasong/schemas'
import type { FC, SyntheticEvent } from 'react'
import { useState } from 'react'
import { TextField } from '../primitives/text-field.js'
import { useAutocomplete } from '../hooks/use-autocomplete.js'
import styles from './entity-filter.module.css'

export interface EntityFilterProps {
  className?: string
  types?: EntityType[]
  value?: EntityType[]
  onChange?(event: SyntheticEvent<Element, Event>, types: EntityType[]): void
}

export const EntityFilter: FC<EntityFilterProps> = props => {
  const { className, value = [], onChange, types = entityTypeNames } = props

  const [inputValue, setInputValue] = useState('')

  const availableOptions = types.filter(t => !value.includes(t))

  const { getRootProps, getInputProps, getListboxProps, getOptionProps, groupedOptions } = useAutocomplete({
    options: availableOptions,
    open: true,
    getOptionLabel: option => option,
    onChange: (ev, option) => {
      if (!option || !onChange) {return}
      onChange(ev, [...value, option as EntityType])
      setInputValue('')
    },
    filterOptions: (options, state) => {
      const input = state.inputValue.toLowerCase().trim()
      if (!input) {return options}
      return options.filter(o => o.toLowerCase().includes(input))
    },
  })

  const handleRemove = (ev: React.MouseEvent, type: EntityType) => {
    onChange?.(ev, value.filter(v => v !== type))
  }

  const inputProps = getInputProps()

  const rootClasses = [styles.root, className].filter(Boolean).join(' ')

  return (
    <div className={rootClasses}>
      <div {...getRootProps()} className={styles.inputRow}>
        {value.map(type => (
          <span
            key={type}
            className={styles.chip}
          >
            {type}
            <span className={styles.chipRemove} onClick={ev => handleRemove(ev, type)}>
              ×
            </span>
          </span>
        ))}
        <TextField
          {...inputProps}
          value={inputValue}
          onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
            setInputValue(ev.target.value)
            inputProps.onChange?.(ev)
          }}
          variant="standard"
          placeholder="Filter..."
          size="small"
          className={styles.filterInput}
        />
      </div>
      {inputValue && groupedOptions.length > 0 ? (
        <div
          {...getListboxProps()}
          className={styles.listbox}
        >
          {groupedOptions.map((option, index) => (
            <div
              key={option}
              {...getOptionProps({ option, index })}
              className={styles.option}
            >
              {option}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default EntityFilter
